import { useEffect, useRef, useState } from "react";
import { X, FileText, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";

/**
 * Secure PDF viewer.
 *
 * Buyer side: hides download / print / save controls using the native viewer
 * flags (#toolbar=0&navpanes=0) and streams the PDF through our authed proxy
 * (/api/files/...) so the real storage URL is never exposed. Right-click,
 * drag, and the Ctrl+S shortcut are suppressed on the overlay. A determined
 * attacker can always screenshot a rendered page — note this as the
 * industry-wide limit, not a bug.
 */
export default function PdfViewer({ src, allowDownload = false, filename = "document.pdf", onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        // Our storage proxy returns the PDF bytes; fetch as blob so we never
        // put a real file URL in the DOM. If src is already a data URL (old
        // base64 posts), convert directly.
        if (src.startsWith("data:")) {
          const resp = await fetch(src);
          const blob = await resp.blob();
          currentUrl = URL.createObjectURL(blob);
        } else {
          // Strip any absolute origin so the api client re-adds BACKEND_URL
          const rel = src.replace(/^https?:\/\/[^/]+/, "");
          const url = rel.startsWith("/api/") ? rel.slice(4) : rel;
          const resp = await api.get(url, { responseType: "blob" });
          currentUrl = URL.createObjectURL(resp.data);
        }
        if (!cancelled) setBlobUrl(currentUrl);
      } catch (e) {
        if (!cancelled) setErr("Could not open document");
      }
    })();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [src]);

  // Block the obvious save shortcuts while the viewer is focused
  useEffect(() => {
    const h = (e) => {
      if (!allowDownload && (e.ctrlKey || e.metaKey) && ["s", "p"].includes(e.key.toLowerCase())) {
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [allowDownload]);

  const viewerSrc = blobUrl ? `${blobUrl}#toolbar=${allowDownload ? 1 : 0}&navpanes=0&statusbar=0&view=FitH` : null;
  const isMobile = typeof navigator !== "undefined" && /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent || "");

  return (
    <div data-testid="pdf-viewer" className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col"
      onContextMenu={allowDownload ? undefined : (e) => e.preventDefault()}>
      <div className="flex items-center justify-between px-4 md:px-8 py-3 text-white">
        <div className="flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4" />
          <span className="font-semibold truncate max-w-[60vw]">{filename}</span>
          {!allowDownload && (
            <span className="hidden sm:inline-block ml-3 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-400/40 rounded-full px-2 py-0.5">View-only</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {blobUrl && (
            <a data-testid="pdf-viewer-open-tab" href={blobUrl} target="_blank" rel="noreferrer"
               className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 flex items-center text-xs">
              Open in new tab
            </a>
          )}
          <button data-testid="pdf-viewer-close" onClick={onClose} aria-label="Close"
            className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div ref={containerRef} className="flex-1 relative overflow-hidden select-none">
        {err && <div className="absolute inset-0 flex items-center justify-center text-white">{err}</div>}
        {!blobUrl && !err && (
          <div className="absolute inset-0 flex items-center justify-center text-white gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading document…
          </div>
        )}
        {viewerSrc && (
          // <object> renders PDFs on Android Chrome where <iframe> only shows a
          // black screen. Falls back to a tap-through "Open in new tab" prompt
          // for the few browsers that don't ship a built-in PDF viewer.
          <object
            data-testid="pdf-viewer-frame"
            data={viewerSrc}
            type="application/pdf"
            className="w-full h-full bg-white"
          >
            <div className="h-full w-full flex items-center justify-center p-6 bg-white text-center">
              <div>
                <div className="text-sm text-gray-700">
                  {isMobile
                    ? "Your browser can't preview PDFs here."
                    : "This browser can't display the PDF inline."}
                </div>
                <a href={blobUrl} target="_blank" rel="noreferrer"
                   className="mt-4 inline-block px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold text-sm">
                  Open PDF in new tab
                </a>
              </div>
            </div>
          </object>
        )}
      </div>
    </div>
  );
}
